import React, { useState } from 'react';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';

import adminApi from '../../Api/adminApi';
import PaginatedTable from '../../MyComponents/PaginatedTable/PaginatedTable';
import useAdminStyles from './adminStyles';
import useAdminQuery from './useAdminQuery';
import StateCard from './StateCard';
import { fmtDate, fmtDateTime, fmtMoney } from './adminFormat';

const EVENT_TYPES = [
    'invoice.paid',
    'invoice.payment_failed',
    'invoice.payment_action_required',
    'customer.subscription.created',
    'customer.subscription.updated',
    'customer.subscription.deleted',
    'checkout.session.completed',
];

/**
 * Every Stripe webhook event the server stored, newest first, with the user it
 * belongs to. Clicking an email opens that user's drawer.
 */
const StripeEventsTable = ({ onOpenUser }) => {
    const classes = useAdminStyles();
    const [eventType, setEventType] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    const query = { eventType, limit: rowsPerPage, offset: page * rowsPerPage };
    const { data, isLoading, isError, isForbidden } = useAdminQuery(() => adminApi.getStripeEvents(query), [
        JSON.stringify(query),
    ]);

    const columns = [
        { id: 'createdAt', label: 'Received', sortable: false, render: (val) => fmtDateTime(val) },
        { id: 'eventType', label: 'Event', sortable: false },
        {
            id: 'email',
            label: 'User',
            sortable: false,
            render: (val, row) =>
                val ? (
                    <button
                        type="button"
                        className={classes.link}
                        style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer' }}
                        onClick={(event) => {
                            event.stopPropagation();
                            onOpenUser(row.userId);
                        }}
                    >
                        {val}
                    </button>
                ) : (
                    <span className={classes.mono}>{row.stripeCustomerId || '—'}</span>
                ),
        },
        {
            id: 'amount',
            label: 'Amount',
            numeric: true,
            sortable: false,
            render: (val, row) => fmtMoney(val, row.currency),
        },
        { id: 'status', label: 'Status', sortable: false, render: (val) => val || '—' },
        {
            id: 'periodEnd',
            label: 'Period end',
            sortable: false,
            render: (val, row) => (val ? `${fmtDate(val)}${row.cancelAtPeriodEnd ? ' (cancels)' : ''}` : '—'),
        },
        {
            id: 'hostedUrl',
            label: 'Invoice',
            sortable: false,
            render: (val) =>
                val ? (
                    <a className={classes.link} href={val} target="_blank" rel="noopener noreferrer">
                        Open
                    </a>
                ) : (
                    '—'
                ),
        },
    ];

    if (isForbidden || (isError && !data)) {
        return <StateCard isError={isError} isForbidden={isForbidden} />;
    }

    return (
        <PaginatedTable
            title={`Stripe events (${data?.count || 0})`}
            toolbarContent={
                <FormControl variant="outlined" size="small" className={classes.filterControl} style={{ minWidth: 240 }}>
                    <InputLabel id="admin-stripe-type">Event type</InputLabel>
                    <Select
                        labelId="admin-stripe-type"
                        label="Event type"
                        value={eventType}
                        onChange={(event) => {
                            setEventType(event.target.value);
                            setPage(0);
                        }}
                    >
                        <MenuItem value="">All events</MenuItem>
                        {EVENT_TYPES.map((t) => (
                            <MenuItem key={t} value={t}>
                                {t}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            }
            headCells={columns}
            rows={data?.events || []}
            rowCount={data?.count || 0}
            order="desc"
            orderBy="createdAt"
            setOrder={() => {}}
            page={page}
            setPage={setPage}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={(n) => {
                setRowsPerPage(n);
                setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
            isLoading={isLoading}
            emptyMessage="No Stripe events stored."
        />
    );
};

export default StripeEventsTable;
